// The post-a-job flow appends the client's answers to the job description as
// `**Question**\nAnswer` blocks, so the spec markdown is the only place the
// structured answers can be read from when displaying a job.
const HEADING_PATTERN = /^[ \t]*\*\*([^\n]+?)\*\*[ \t]*$/gm;

const ABOUT_CLIENT_HEADING = 'who are you';

const CAPACITY_VALUES = [
  {
    keywords: ['client or customer', 'another company'],
    value: 'For a client',
  },
  { keywords: ['personal project', 'an individual'], value: 'Individual' },
  { keywords: ['company'], value: 'A company' },
];

const capitalizeFirst = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const formatCapacity = (answer) => {
  const text = answer.toLowerCase();
  const match = CAPACITY_VALUES.find(({ keywords }) =>
    keywords.some((keyword) => text.includes(keyword))
  );

  return match ? match.value : capitalizeFirst(answer);
};

// Answers are written in the first person, e.g. "I'm ready to make a paid hire".
// Only the "I'm"/"I am" contraction is dropped: "I will possibly make a paid
// hire" keeps its leading "I".
const formatHiringStage = (answer) =>
  capitalizeFirst(answer.replace(/^i(?:['’]m| am)\s+/i, ''));

const formatWorkType = (answer, heading = '') => {
  // Short stored headings are the work type. The full question also contains
  // "larger project", so only this exact heading is treated as the answer.
  if (heading.toLowerCase().trim() === 'larger project') {
    return 'Ongoing project';
  }

  const text = (answer || '').toLowerCase();

  if (text.includes('ongoing') || text.includes('larger project')) {
    return 'Ongoing project';
  }

  if (text.includes('no follow')) {
    return 'Single job, no follow-up';
  }

  if (text.includes('follow')) {
    return 'Single job, potential follow-up';
  }

  return capitalizeFirst(answer);
};

// Levels are picked as hourly-rate bands, e.g. "Mid-level (Industry Average):
// $35-75", and several can be chosen at once.
const formatExperienceLevel = (answer) => {
  const levels = answer
    .split(',')
    .map((part) =>
      part
        .split(':')[0]
        .replace(/\([^)]*\)/g, '')
        .trim()
    )
    .filter(Boolean);

  const unique = levels.filter(
    (level, index) => levels.indexOf(level) === index
  );

  if (!unique.length) {
    return '';
  }

  return [
    capitalizeFirst(unique[0]),
    ...unique.slice(1).map((level) => level.toLowerCase()),
  ].join(', ');
};

// The budget question is free text such as "$750 – $1,500", "£500", or
// "Receive offers". Only monetary answers produce a budget value; anything
// without an amount is treated as open (the chip falls back to "Open to
// offers"). The spaced dash the client typed is normalised to a tight en-dash,
// and a range whose upper bound omits the currency symbol ("$250 – 750") keeps
// the symbol on both ends.
const AMOUNT = String.raw`\d[\d,]*(?:\.\d+)?`;
const RANGE_PATTERN = new RegExp(
  `([£$€])\\s?(${AMOUNT})\\s*(?:[-–—]|to)\\s*([£$€])?\\s?(${AMOUNT})`,
  'i'
);
const SINGLE_PATTERN = new RegExp(`[£$€]\\s?${AMOUNT}`);

const formatBudgetRange = (answer) => {
  const range = answer.match(RANGE_PATTERN);

  if (range) {
    const [, symbol, low, upperSymbol, high] = range;
    return `${symbol}${low}–${upperSymbol || symbol}${high}`;
  }

  const single = answer.match(SINGLE_PATTERN);

  return single ? single[0].replace(/\s+/g, '') : '';
};

const POSTFLOW_HEADINGS = [
  'Budget range:',
  'Examples:',
  'Who are you, and what do you do?',
  'In what capacity are you hiring?',
  'Where are you in the hiring process?',
  'What type of work is this?',
  'Is this an independent one off job, or part of a larger project?',
  'One off job',
  'Larger project',
  'What experience level is needed?:',
  'What experience level is needed?',
];

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Some stored specs concatenate the first Q&A heading onto the last
// description sentence, with no space before the bold markers. Only split
// that glued, end-of-line form so ordinary prose - including a client
// writing "What type of work is this?" - stays in the description.
const detachGluedHeadings = (spec) =>
  POSTFLOW_HEADINGS.reduce((text, heading) => {
    const pattern = new RegExp(
      `([^\\s])(\\*\\*${escapeRegExp(heading)}\\*\\*)(?=[ \\t]*(?:\\n|$))`,
      'gi'
    );

    return text.replace(pattern, '$1\n\n$2');
  }, spec);

const DETAIL_FIELDS = [
  {
    label: 'Hiring as',
    matches: (heading) => heading.includes('capacity'),
    format: formatCapacity,
  },
  {
    label: 'Hiring stage',
    matches: (heading) => heading.includes('hiring process'),
    format: formatHiringStage,
  },
  {
    label: 'Work type',
    matches: (heading) =>
      heading.includes('type of work') ||
      heading.includes('one off job') ||
      heading.includes('larger project'),
    format: formatWorkType,
  },
  {
    label: 'Experience level',
    matches: (heading) => heading.includes('experience level'),
    format: formatExperienceLevel,
  },
];

const findSections = (spec) => {
  const pattern = new RegExp(HEADING_PATTERN);
  const headings = [];
  let match = pattern.exec(spec);

  while (match !== null) {
    headings.push({
      heading: match[1].trim(),
      start: match.index,
      answerStart: match.index + match[0].length,
    });
    match = pattern.exec(spec);
  }

  return headings.map((item, index) => {
    const next = headings[index + 1];

    return {
      heading: item.heading,
      start: item.start,
      answer: spec
        .slice(item.answerStart, next ? next.start : spec.length)
        .trim(),
    };
  });
};

/**
 * Splits a job spec into the parts the job details page renders separately: the
 * description markdown, the client's free-text introduction, and the short
 * factual answers shown as a details grid.
 *
 * The budget is returned separately so the chip row can show the range the
 * client entered, but it is intentionally left in the description too: unlike
 * the details-grid fields, the "Budget range" block is not removed from the body.
 *
 * @param {string} spec - The job spec markdown.
 * @returns {{ description: string, aboutClient: string, budget: (string|null), details: Array<{ label: string, value: string }> }}
 */
export function parseBriefSpec(spec = '') {
  const normalized = detachGluedHeadings(spec);
  const sections = findSections(normalized);

  if (!sections.length) {
    return { description: spec, aboutClient: '', budget: null, details: [] };
  }

  const parts = [normalized.slice(0, sections[0].start).trim()];
  const details = [];
  let aboutClient = '';
  // `null` means the job predates the budget question, so the chip should fall
  // back to the structured amount; `''` means the client answered but is open.
  let budget = null;

  sections.forEach(({ heading, answer }) => {
    const key = heading.toLowerCase();
    const field = DETAIL_FIELDS.find(({ matches }) => matches(key));

    if (field) {
      const value = field.format(answer, heading);

      if (value) {
        details.push({ label: field.label, value });
      }

      return;
    }

    if (key.includes(ABOUT_CLIENT_HEADING)) {
      aboutClient = answer;
      return;
    }

    if (budget === null && key.includes('budget')) {
      budget = formatBudgetRange(answer);
    }

    parts.push(`**${heading}**\n${answer}`);
  });

  return {
    description: parts.filter(Boolean).join('\n\n'),
    aboutClient,
    budget,
    details,
  };
}
